import React from 'react';
import { TbExternalLink } from 'react-icons/tb';
import { useParams } from 'react-router-dom';

import { useFetchRestaurantQuery } from '@/api/hooks/useRestaurant';
import { formatOpeningHours } from '@/utils/formatOpeningHours';

import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { Button } from '@/components/molecules/Button';
import { LeafletMap } from '@/components/molecules/LeafletMap';
import { RestaurantForm } from '@/components/organisms/RestaurantForm';
import { SocialNetworkForm } from '@/components/organisms/SocialNetworkForm';
import { UserForm } from '@/components/organisms/UserForm';
import { TabPageLayout } from '@/components/templates/TabPageLayout';

export const EditPage = () => {
  const { id } = useParams();
  const restaurantId = id || '';

  const { data: restaurant, isLoading } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });

  if (isLoading || !restaurant || !restaurantId) {
    return (
      <div className="flex flex-auto justify-center items-center h-48">
        <Spinner reverse={true} big />
      </div>
    );
  }

  return (
    <TabPageLayout firstTitle={'Informations sur votre restaurant'} secondTitle={restaurant.name} restaurantId={restaurant.id}>
      <section className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-5">
          Votre fiche restaurant
        </Typography>
        <RestaurantForm restaurant={restaurant} />
      </section>

      <section className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-5">
          Vos réseaux sociaux
        </Typography>

        <SocialNetworkForm restaurant={restaurant} />
      </section>

      <section className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-5">
          Vos informations personnelles
        </Typography>

        <UserForm restaurant={restaurant} />
      </section>

      <section className="border border-gray-300 gap-5 p-4 md:p-10 rounded-xl">
        <Typography variant="h1" weight="semibold" marginClassName="mb-5">
          Informations Google Maps
        </Typography>
        <Typography variant="h3" color="gray">
          ⚠️ Les informations ci-dessous sont récupérées automatiquement depuis Google Maps.
        </Typography>
        <Typography variant="h4" color="gray">
          Veuillez modifier votre fiche Google pour les mettre à jour.
        </Typography>
        <div className="grid md:grid-cols-7 gap-10 mt-10">
          <div className=" col-span-4 flex flex-col">
            <Typography variant="h3" color="gray" weight="semibold">
              Localisation du restaurant
            </Typography>
            <Typography variant="h4" color="gray" marginClassName="my-2">
              {restaurant.formattedAddress}
            </Typography>

            <div className="w-full h-full overflow-auto rounded-xl">
              <LeafletMap
                height="240px"
                width="100%"
                defaultZoom={10}
                maxZoom={20}
                minZoom={5}
                initialCenter={{
                  latitude: restaurant.location.lat,
                  longitude: restaurant.location.lng
                }}
                disableCopyright
              />
            </div>
          </div>
          <div className="w-full col-span-3 max-w-3xl space-y-5">
            {restaurant.simpleOpeningHours && (
              <div className="flex flex-col">
                <Typography variant="h3" color="gray" weight="semibold">
                  Horaires d'ouverture
                </Typography>
                <Typography variant="h4" color="gray" marginClassName="mt-2">
                  {formatOpeningHours(restaurant.simpleOpeningHours)}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col md:flex-row gap-5 justify-between my-5">
          {restaurant.website && (
            <div className="flex flex-col w-full">
              <Typography variant="h3" color="gray" weight="semibold">
                Site web
              </Typography>
              <Typography variant="h4" color="gray" marginClassName="mt-2" breakWords="all">
                {restaurant.website}
              </Typography>
            </div>
          )}
          <div className="flex flex-col w-full">
            <Typography variant="h3" color="gray" weight="semibold">
              Numéro de téléphone du restaurant
            </Typography>
            <Typography variant="h4" color="gray" marginClassName="mt-2">
              {restaurant.phoneNumber ? restaurant.phoneNumber : 'Non renseigné'}
            </Typography>
          </div>
        </div>

        <Button
          label="Modifier la localisation/les horaires/le numéro/le site web sur Google Maps"
          color="primary"
          fullWidth
          icon={<TbExternalLink className="text-lg mr-1" />}
          onClick={() => window.open(`https://www.google.com/maps/place/${restaurant.gmapUrl}`, '_blank')}
        />
      </section>
    </TabPageLayout>
  );
};
